import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  CheckCircle2,
  CornerDownRight,
  Globe,
  MapPin,
  MessageSquareReply,
  MoreHorizontal,
  PenTool,
  RotateCcw,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import {
  useCreateComment,
  useDeleteComment,
  useEditComment,
  useResolveComment,
  useUnresolveComment,
  type WatchComment,
} from "@/hooks/use-watch";
import type { CommentSort } from "./right-panel";
import { useMentionableMembers } from "@/hooks/use-mentionable";
import { convertMentionsToTokens, mentionTokensToDisplay, renderCommentContent } from "@/lib/mentions";
import { MentionTextarea } from "./mention-textarea";

type Props = {
  mode: "workspace" | "guest" | "share";
  comments: WatchComment[];
  sort: CommentSort;
  /** Video id used for hook invalidation. workspace = current videoId, guest = share token. */
  watchKey: string | undefined;
  /** Version id the viewer is currently on — passed to /resolve so backend knows which V was viewed. */
  currentVersionId: string | null;
  /** Click on the yellow timecode chip → seek video. */
  onSeek: (seconds: number) => void;
  /** Selection shared with the timeline. When it changes, the matching card
      scrolls into view + gets the primary-tinted border. */
  selectedCommentId?: string | null;
  onSelectComment?: (id: string) => void;
  /** Whether commenting/replying is allowed (share commentAllowed gate). Default true. */
  canComment?: boolean;
  /** Asset type of the version. Image comments naturally omit the timecode
   *  chip since they carry `timestampSeconds: null` — the chip is already
   *  guarded on that field. Prop accepted for API symmetry with the composer
   *  and for future asset-type-specific empty-state copy. */
  assetType?: "video" | "audio" | "image";
};

/**
 * Comment list rendering (Task 10, replies added 2026-07-12).
 *
 * Sort in this file drives DISPLAY order only. Sequence badges (#N) come from
 * createdAt-asc order of ROOT comments regardless of sort — flipping to Oldest
 * first doesn't relabel the #Ns, so a comment's identity stays stable across
 * sort toggles. Replies aren't numbered (they inherit their parent's #N).
 *
 * Threading (Frame.io-style, depth 1):
 *   - Backend caps at one level: a reply cannot itself be replied to.
 *   - Replies inherit the parent's anchor/annotation — they have no timeline
 *     dot of their own. They render as an indented list under each root.
 *   - Reply button is available to workspace + guest modes (Frame.io allows
 *     guests to reply too, matching the reference in the screenshot).
 *
 * Permissions matrix (text comments only per spec):
 *   Action        | Owner | Workspace admin | Other
 *   Edit          | ✅    | ❌              | ❌
 *   Delete        | ✅    | ✅              | ❌
 *   Mark complete | ✅    | ✅              | ❌
 *
 * Guest mode: no edit/delete/resolve on individual cards — hides the hover strip.
 * Guests CAN still post replies via the inline reply composer.
 */
export function CommentList({
  mode,
  comments,
  sort,
  watchKey,
  currentVersionId,
  onSeek,
  selectedCommentId = null,
  onSelectComment,
  assetType,
  canComment = true,
}: Props) {
  const isImage = assetType === "image";
  // Split into roots + replies, then number roots by createdAt-asc.
  const { numberedRoots, repliesByParent } = useMemo(() => {
    const roots: WatchComment[] = [];
    const replies: WatchComment[] = [];
    for (const c of comments) {
      if (c.parentCommentId) replies.push(c);
      else roots.push(c);
    }

    // Sequence numbers stable across sort toggles: always by root createdAt-asc.
    const seq = [...roots]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((c, i) => ({ comment: c, sequence: i + 1 }));

    // Display order — three modes:
    //   timecode: timestampSeconds ASC, nulls sink to the bottom (no timeline
    //             anchor), createdAt ASC as tiebreak. Matches Frame.io default.
    //   oldest:   createdAt ASC (already in seq order).
    //   newest:   createdAt DESC.
    let orderedSeq: { comment: WatchComment; sequence: number }[];
    if (sort === "timecode") {
      orderedSeq = [...seq].sort((a, b) => {
        const tA = a.comment.timestampSeconds ?? Number.POSITIVE_INFINITY;
        const tB = b.comment.timestampSeconds ?? Number.POSITIVE_INFINITY;
        if (tA !== tB) return tA - tB;
        return new Date(a.comment.createdAt).getTime() - new Date(b.comment.createdAt).getTime();
      });
    } else if (sort === "oldest") {
      orderedSeq = seq;
    } else {
      orderedSeq = [...seq].reverse();
    }

    // Group replies by parent, oldest → newest inside each thread.
    const byParent = new Map<string, WatchComment[]>();
    for (const r of replies) {
      if (!r.parentCommentId) continue;
      const bucket = byParent.get(r.parentCommentId) ?? [];
      bucket.push(r);
      byParent.set(r.parentCommentId, bucket);
    }
    for (const bucket of byParent.values()) {
      bucket.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }

    return { numberedRoots: orderedSeq, repliesByParent: byParent };
  }, [comments, sort]);

  // Selection is now controlled by the parent (WatchShell) so the timeline
  // dot and the comment card can stay in sync. Fall back to local state when
  // no parent handler is wired (defensive, though every usage passes it).
  const [uncontrolledSelectedId, setUncontrolledSelectedId] = useState<string | null>(null);
  const selectedId = onSelectComment ? selectedCommentId : uncontrolledSelectedId;

  // Auto-scroll the selected card into view when selection changes from
  // outside (e.g. user clicked a timeline dot). One ref per card, keyed by id.
  const cardRefs = useRef<Map<string, HTMLLIElement | null>>(new Map());
  useEffect(() => {
    if (!selectedId) return;
    const node = cardRefs.current.get(selectedId);
    node?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedId]);

  // Which root has its inline reply composer open. At most one at a time so
  // the right panel doesn't turn into a wall of textareas.
  const [replyingToId, setReplyingToId] = useState<string | null>(null);

  if (comments.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
        <div className="text-sm font-medium text-foreground">No comments yet</div>
        <div className="text-xs text-muted-foreground">
          {isImage
            ? "Drop an anchor pin or leave a general note from the composer below."
            : "Comment on a specific timecode from the composer below."}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-2">
      <ul className="flex flex-col gap-1.5">
        {numberedRoots.map(({ comment, sequence }) => (
          <li
            key={comment.id}
            ref={(el) => {
              if (el) cardRefs.current.set(comment.id, el);
              else cardRefs.current.delete(comment.id);
            }}
          >
            <CommentCard
              mode={mode}
              comment={comment}
              sequence={sequence}
              watchKey={watchKey}
              currentVersionId={currentVersionId}
              onSeek={onSeek}
              selected={selectedId === comment.id}
              onSelect={() => {
                if (onSelectComment) onSelectComment(comment.id);
                else setUncontrolledSelectedId(comment.id);
                if (typeof comment.timestampSeconds === "number") {
                  onSeek(comment.timestampSeconds);
                }
              }}
              canComment={canComment}
              replies={repliesByParent.get(comment.id) ?? []}
              replyOpen={replyingToId === comment.id}
              onOpenReply={() => setReplyingToId(comment.id)}
              onCloseReply={() => setReplyingToId(null)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Card ────────────────────────────────────────────────────────────────────

function CommentCard({
  mode,
  comment,
  sequence,
  watchKey,
  currentVersionId,
  onSeek,
  selected,
  onSelect,
  canComment,
  replies,
  replyOpen,
  onOpenReply,
  onCloseReply,
}: {
  mode: "workspace" | "guest" | "share";
  comment: WatchComment;
  sequence: number;
  watchKey: string | undefined;
  currentVersionId: string | null;
  onSeek: (seconds: number) => void;
  selected: boolean;
  onSelect: () => void;
  canComment: boolean;
  replies: WatchComment[];
  replyOpen: boolean;
  onOpenReply: () => void;
  onCloseReply: () => void;
}) {
  const { user, role } = useAuth();
  const isWorkspaceAdmin = role === "ADMIN";
  // Owner check uses userId (backend formatComment() now returns it). Two
  // people sharing a display name no longer share Edit affordances.
  const isOwner =
    mode === "workspace" &&
    !!user &&
    comment.author.type === "workspace" &&
    comment.author.userId === user.id;
  const hasAnnotation = !!comment.annotationsUrl;
  const hasAnchor = comment.positionX != null && comment.positionY != null;
  const isDrawnComment = hasAnnotation || hasAnchor;

  // Guest ownership can't be checked precisely from the client (no exposed
  // guest id in the response), so in guest mode we optimistically show
  // Delete for guest-authored comments; server rejects with 403 if not the
  // actual owner. Frame.io does the same.
  const isGuestSelf = mode !== "workspace" && comment.author.type === "guest";

  // Both modes get the hover strip now. Guest permissions per Frame.io:
  //   Edit  — workspace author only. Guests can't edit.
  //   Delete — workspace: author OR admin. Guest: guest-authored only.
  //   Mark complete / reopen — anyone with access can resolve any comment.
  const showHoverStrip = true;
  const canEdit = mode === "workspace" && isOwner && !isDrawnComment;
  const canDelete = (mode === "workspace" && (isOwner || isWorkspaceAdmin)) || isGuestSelf;
  const canResolve = showHoverStrip;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => mentionTokensToDisplay(comment.content));
  const { data: mentionMembers } = useMentionableMembers({ mode, videoId: currentVersionId, watchKey });
  const editMembers = mentionMembers ?? [];

  const edit = useEditComment(watchKey);
  const del = useDeleteComment(mode, watchKey);
  const resolve = useResolveComment(mode, watchKey);
  const unresolve = useUnresolveComment(mode, watchKey);

  function submitEdit() {
    const trimmed = draft.trim();
    const content = convertMentionsToTokens(trimmed, editMembers);
    if (!trimmed || content === comment.content) {
      setEditing(false);
      setDraft(mentionTokensToDisplay(comment.content));
      return;
    }
    edit.mutate(
      { id: comment.id, content },
      { onSuccess: () => setEditing(false) },
    );
  }

  function toggleResolve() {
    if (comment.resolved) {
      unresolve.mutate(comment.id);
    } else if (currentVersionId) {
      resolve.mutate({ id: comment.id, resolvedByVideoId: currentVersionId });
    }
  }

  return (
    <div
      className={cn(
        // Base card look — rounded box with subtle border, hover/select boosts contrast.
        "group relative rounded-lg border transition-colors",
        selected
          ? "border-primary/50 bg-primary/5"
          : "border-transparent hover:border-border hover:bg-secondary/40",
        comment.resolved && "bg-secondary/40 opacity-70",
      )}
    >
      {/* Main row (clickable for select+seek) */}
      <div
        onClick={editing ? undefined : onSelect}
        role="button"
        tabIndex={0}
        className={cn(
          "relative flex cursor-pointer gap-3 p-3",
          editing && "cursor-default",
        )}
      >
        {/* Avatar */}
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/20 text-xs font-semibold uppercase text-primary">
          {initials(comment.author.name || (comment.author.type === "guest" ? "Guest" : "?"))}
        </div>

        <div className="min-w-0 flex-1">
          {/* Header line: name + relative time + #N + visibility icon */}
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm">
            <span className="truncate font-medium text-foreground">
              {comment.author.name || (comment.author.type === "guest" ? "Guest" : "Someone")}
            </span>
            <span className="text-xs text-muted-foreground">{relativeTime(comment.createdAt)}</span>
            <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
              <span>#{sequence}</span>
              {comment.visibility === "internal" ? (
                <Users
                  className="h-3 w-3 text-amber-500"
                  aria-label="Internal comment"
                />
              ) : (
                <Globe className="h-3 w-3" aria-label="Public comment" />
              )}
            </span>
          </div>

          {/* Chips row: timecode + anchor indicator + drawn indicator */}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {comment.timestampSeconds != null && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSeek(comment.timestampSeconds!);
                }}
                className="inline-flex items-center rounded-md bg-yellow-400/20 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-yellow-500 transition-colors hover:bg-yellow-400/30"
                title="Jump to this time"
              >
                {formatTimecode(comment.timestampSeconds)}
              </button>
            )}
            {hasAnchor && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <MapPin className="h-3 w-3" />
                anchor
              </span>
            )}
            {hasAnnotation && !hasAnchor && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <PenTool className="h-3 w-3" />
                annotation
              </span>
            )}
            {comment.commentedOnVersionNumber != null && (
              <span className="text-[11px] text-muted-foreground">
                V{comment.commentedOnVersionNumber}
              </span>
            )}
          </div>

          {/* Body — text OR edit textarea */}
          {editing ? (
            <div className="mt-2">
              <MentionTextarea
                value={draft}
                onChange={setDraft}
                members={editMembers}
                onSubmitKey={submitEdit}
                onEscape={() => {
                  setEditing(false);
                  setDraft(mentionTokensToDisplay(comment.content));
                }}
                rows={3}
                autoFocus
                className="w-full resize-none rounded-md border border-border bg-background p-2 text-sm text-foreground outline-none focus:border-ring"
              />
              <div className="mt-1 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setDraft(mentionTokensToDisplay(comment.content));
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitEdit}
                  disabled={edit.isPending || !draft.trim()}
                  className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
                >
                  {edit.isPending ? "Saving..." : "Save"}
                </button>
              </div>
              {edit.error && (
                <div className="mt-1 text-xs text-destructive">
                  {(edit.error as Error).message || "Save failed"}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-1.5 whitespace-pre-wrap break-words text-sm text-foreground">
              {renderCommentContent(comment.content)}
            </div>
          )}

          {/* Resolution footer if resolved */}
          {comment.resolved && comment.resolvedBy && (
            <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3 w-3" />
              Marked complete
              {comment.resolvedBy.name && ` by ${comment.resolvedBy.name}`}
              {comment.resolvedBy.onVersionNumber != null && ` on V${comment.resolvedBy.onVersionNumber}`}
            </div>
          )}

          {/* Bottom action row — Reply left, three-dot menu + mark-complete
              right. Merged from the old top-right hover strip so all actions
              sit on the same line. Reply is always visible in both modes;
              the workspace-only strip stays hidden until hover / selection so
              the card doesn't clutter at rest. */}
          {!editing && (
            <div className="mt-1.5 flex items-center justify-between">
              {!comment.resolved && canComment ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (replyOpen) onCloseReply();
                    else onOpenReply();
                  }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <MessageSquareReply className="h-3 w-3" />
                  Reply
                </button>
              ) : (
                <span />
              )}

              {showHoverStrip && (canEdit || canDelete || canResolve) && (
                <div
                  className={cn(
                    // Always laid out so the row's width is stable — the
                    // strip doesn't push Reply around on hover. Fades in
                    // with opacity + becomes clickable only when hovered
                    // or the card is selected.
                    "flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5 shadow-sm transition-opacity",
                    selected
                      ? "opacity-100"
                      : "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
                  )}
                >
                  {(canEdit || canDelete) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        aria-label="More"
                        className="grid h-6 w-6 place-items-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        side="top"
                        align="end"
                        sideOffset={4}
                        avoidCollisions={false}
                        className="w-32"
                      >
                        {canEdit && (
                          <DropdownMenuItem onSelect={() => setEditing(true)}>Edit</DropdownMenuItem>
                        )}
                        {canDelete && (
                          <DropdownMenuItem
                            onSelect={() => del.mutate(comment.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {canResolve && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleResolve();
                      }}
                      disabled={resolve.isPending || unresolve.isPending}
                      aria-label={comment.resolved ? "Reopen" : "Mark complete"}
                      title={comment.resolved ? "Reopen" : "Mark complete"}
                      className={cn(
                        "grid h-6 w-6 place-items-center rounded transition-colors",
                        comment.resolved
                          ? "text-emerald-500 hover:bg-emerald-500/10"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                      )}
                    >
                      {comment.resolved ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reply thread — indented, avatar-flush. Rendered even when resolved so
          historical context stays readable; new replies are hidden by the
          composer being hidden above. */}
      {(replies.length > 0 || replyOpen) && (
        <div className="ml-11 mr-3 mb-2 border-l border-border/60 pl-3">
          {replies.map((r) => (
            <ReplyRow
              key={r.id}
              mode={mode}
              reply={r}
              currentVersionId={currentVersionId}
              watchKey={watchKey}
            />
          ))}

          {replyOpen && currentVersionId && canComment && (
            <InlineReplyComposer
              mode={mode}
              watchKey={watchKey}
              currentVersionId={currentVersionId}
              parentCommentId={comment.id}
              onDone={onCloseReply}
              onCancel={onCloseReply}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Reply row ──────────────────────────────────────────────────────────────

function ReplyRow({
  mode,
  reply,
  currentVersionId,
  watchKey,
}: {
  mode: "workspace" | "guest" | "share";
  reply: WatchComment;
  currentVersionId: string | null;
  watchKey: string | undefined;
}) {
  const { user, role } = useAuth();
  const isWorkspaceAdmin = role === "ADMIN";
  const isOwner =
    mode === "workspace" &&
    !!user &&
    reply.author.type === "workspace" &&
    reply.author.userId === user.id;
  const isGuestSelf = mode !== "workspace" && reply.author.type === "guest";

  const canEdit = mode === "workspace" && isOwner;
  const canDelete = (mode === "workspace" && (isOwner || isWorkspaceAdmin)) || isGuestSelf;

  const edit = useEditComment(watchKey);
  const del = useDeleteComment(mode, watchKey);
  const { data: mentionMembers } = useMentionableMembers({ mode, videoId: currentVersionId, watchKey });
  const editMembers = mentionMembers ?? [];

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => mentionTokensToDisplay(reply.content));

  function submitEdit() {
    const trimmed = draft.trim();
    const content = convertMentionsToTokens(trimmed, editMembers);
    if (!trimmed || content === reply.content) {
      setEditing(false);
      setDraft(mentionTokensToDisplay(reply.content));
      return;
    }
    edit.mutate({ id: reply.id, content }, { onSuccess: () => setEditing(false) });
  }

  return (
    <div className="group/reply relative flex gap-2 py-2">
      <div className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-muted text-[10px] font-semibold uppercase text-muted-foreground">
        {initials(reply.author.name || (reply.author.type === "guest" ? "Guest" : "?"))}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs">
          <CornerDownRight className="h-3 w-3 text-muted-foreground" />
          <span className="truncate font-medium text-foreground">
            {reply.author.name || (reply.author.type === "guest" ? "Guest" : "Someone")}
          </span>
          <span className="text-muted-foreground">{relativeTime(reply.createdAt)}</span>
        </div>

        {editing ? (
          <div className="mt-1">
            <MentionTextarea
              value={draft}
              onChange={setDraft}
              members={editMembers}
              onSubmitKey={submitEdit}
              onEscape={() => {
                setEditing(false);
                setDraft(mentionTokensToDisplay(reply.content));
              }}
              rows={2}
              autoFocus
              className="w-full resize-none rounded-md border border-border bg-background p-1.5 text-sm text-foreground outline-none focus:border-ring"
            />
            <div className="mt-1 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setDraft(mentionTokensToDisplay(reply.content));
                }}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitEdit}
                disabled={edit.isPending || !draft.trim()}
                className="rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
              >
                {edit.isPending ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-0.5 whitespace-pre-wrap break-words text-sm text-foreground">
            {renderCommentContent(reply.content)}
          </div>
        )}
      </div>

      {(canEdit || canDelete) && !editing && (
        <div className="pointer-events-none absolute right-0 top-1.5 flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5 opacity-0 shadow-sm transition-opacity group-hover/reply:pointer-events-auto group-hover/reply:opacity-100">
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="More"
              className="grid h-5 w-5 place-items-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <MoreHorizontal className="h-3 w-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="end"
              sideOffset={4}
              avoidCollisions={false}
              className="w-32"
            >
              {canEdit && (
                <DropdownMenuItem onSelect={() => setEditing(true)}>Edit</DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onSelect={() => del.mutate(reply.id)}
                  className="text-destructive focus:text-destructive"
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

// ── Inline reply composer ──────────────────────────────────────────────────

function InlineReplyComposer({
  mode,
  watchKey,
  currentVersionId,
  parentCommentId,
  onDone,
  onCancel,
}: {
  mode: "workspace" | "guest" | "share";
  watchKey: string | undefined;
  currentVersionId: string;
  parentCommentId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const create = useCreateComment(mode, watchKey);
  const { data: mentionMembers } = useMentionableMembers({
    mode,
    videoId: currentVersionId,
    watchKey,
  });
  const members = mentionMembers ?? [];

  function submit() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    const content = convertMentionsToTokens(trimmed, members);
    create.mutate(
      {
        currentVersionId,
        content,
        // Replies inherit anchor from the parent — backend stores NULLs.
        timestampSeconds: null,
        parentCommentId,
      },
      {
        onSuccess: () => {
          setDraft("");
          onDone();
        },
      },
    );
  }

  const canSend = draft.trim().length > 0 && !create.isPending;

  return (
    <div className="py-2">
      <MentionTextarea
        ref={ref}
        value={draft}
        onChange={setDraft}
        members={members}
        onSubmitKey={submit}
        onEscape={onCancel}
        placeholder={members.length > 0 ? "Write a reply… use @ to mention" : "Write a reply..."}
        rows={2}
        autoFocus
        className="w-full resize-none rounded-md border border-border bg-background p-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
      />
      <div className="mt-1 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSend}
          className={cn(
            "rounded-md px-2 py-1 text-xs font-medium transition-colors",
            canSend
              ? "bg-primary text-primary-foreground hover:bg-primary/90"
              : "bg-secondary text-muted-foreground",
          )}
        >
          {create.isPending ? "Sending..." : "Reply"}
        </button>
      </div>
      {create.error && (
        <div className="mt-1 truncate text-xs text-destructive">
          {(create.error as Error).message || "Failed to send"}
        </div>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const totalMs = Math.floor(seconds * 1000);
  const hh = Math.floor(totalMs / 3_600_000);
  const mm = Math.floor((totalMs % 3_600_000) / 60_000);
  const ss = Math.floor((totalMs % 60_000) / 1000);
  const ms = totalMs % 1000;
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}.${padMs(ms)}`;
}
const pad = (n: number) => n.toString().padStart(2, "0");
const padMs = (n: number) => n.toString().padStart(3, "0");

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return `${diffSec}s`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
